import { ChildOrganizationSummary } from '@shared/api/types';
import { MultiSelect } from '@shared/components/multi-select';
import { useMemo } from 'react';

type Props = {
  organizations: ChildOrganizationSummary[];
  selectedUuids: string[];
  onChange: (uuids: string[]) => void;
};

/**
 * Multi-select for filtering group dashboard pages by child organization.
 *
 * Composes the shared MultiSelect primitive — no new dropdown UI. Empty
 * selection means "all organizations" by convention (Phase 1: filter is
 * persisted but not consumed for data fetching).
 */
export function GroupOrgFilter({
  organizations,
  selectedUuids,
  onChange,
}: Props) {
  const options = useMemo(
    () =>
      organizations
        .map((org) => ({
          label: org.name,
          value: org.uuid,
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
        ),
    [organizations]
  );

  const placeholder =
    organizations.length === 0 ? 'No organisations' : 'All organisations';

  return (
    <MultiSelect
      options={options}
      defaultValue={selectedUuids}
      onValueChange={onChange}
      placeholder={placeholder}
      itemName="organisation"
      className="bg-gray-00"
      autoSize
      responsive={{
        mobile: { maxCount: 1 },
        tablet: { maxCount: 2 },
        desktop: { maxCount: 4 },
      }}
      hideSelectAll
      maxWidth="100%"
    />
  );
}
